import * as React from 'react'
import { connect } from 'react-redux'
import { styling } from 'common/utils'
import { DynamicCx } from 'common/types'
import { KeyCode } from 'common/constants/keyCode'
import hostConfig from 'common/config'
import { RootState } from 'pc/reducer'
import SearchInput from './SearchInput'
import AutoComplete from './AutoComplete'
import DetailSearch from './DetailSearch'
import { AutoCompleteState, openAutoComplete, closeAutoComplete, setSelected } from 'common/ducks/gnb/autoComplete'
import * as s from './gnbSearch.scss'

interface StateProps {
  autoComplete: AutoCompleteState
}

interface DispatchProps {
  openAutoComplete: typeof openAutoComplete
  closeAutoComplete: typeof closeAutoComplete
  setSelected: typeof setSelected
}

interface OwnProps {
  cx?: DynamicCx
}

interface OwnState {
  selectedKeyword: string
  selectedKeywordCategoryId: string
}

type Props = StateProps & DispatchProps & OwnProps

@styling(s)
class GnbSearch extends React.Component<Props, OwnState> {
  constructor(props) {
    super(props)

    this.state = {
      selectedKeyword: '', // 자동완성에서 선택된 값을 임시로 input에 노출하기 위함
      selectedKeywordCategoryId: '', // 자동완성 및 검색창에서 검색 하고자 할때 필요한 카테고리 id
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: OwnState) {
    return (
      nextProps.autoComplete.keyword !== this.props.autoComplete.keyword ||
      nextState.selectedKeyword !== this.state.selectedKeyword ||
      nextState.selectedKeywordCategoryId !== this.state.selectedKeywordCategoryId
    )
  }

  handleSaveKeywordInfo = ({ keyword, categoryId = '' }) => {
    this.setState({
      selectedKeyword: keyword,
      selectedKeywordCategoryId: categoryId,
    })
  }

  handleResetSelectedKeyword = () => {
    this.setState({
      selectedKeyword: '',
      selectedKeywordCategoryId: '',
    })
  }

  // 진입 시점 ?
  // 1. 검색창에서 enter or 검색 버튼 클릭
  // 2. 자동완성 레이어
  //    - 리스트 아이템 클릭
  //    - down or up key로 이동하다가 enter
  handleSearch = ({ keyword, categoryId }) => {
    const searchKeyword = keyword || this.state.selectedKeyword
    const encodeKeyword = encodeURIComponent(searchKeyword)
    const searchCategoryId = categoryId || this.state.selectedKeywordCategoryId

    if (searchCategoryId) {
      window.location.href = `${
        hostConfig.shoppingSearchUri.pc
      }/search/category.nhn?query=${encodeKeyword}&cat_id=${searchCategoryId}`
    } else {
      window.location.href = `${
        hostConfig.shoppingSearchUri.pc
      }/search/all_search.nhn?query=${encodeKeyword}&cat_id=&frm=NVSHATC`
    }
  }

  // 검색창 & 자동완성 레이어 제어 로직
  // keyDown이벤트로 처리 (tab, tab + shift, down key, up key)
  // 1. 자동완성 레이어가 열려 있는 경우에는 tab키 기본기능은 끔
  // 2. 포커스가 자동완성 -> 검색창으로 이동시에 최초 검색한 키워드 노출되어야 함
  // 3. tab, keydown 클릭시: 자동완성 레이어 열기 or 리스트 하나씩 이동
  //    shift + tab, keyup 클릭시: 리스트 하나씩 이동 or 자동완성 레이어 닫기
  handleKeyDown = e => {
    const { autoComplete, openAutoComplete, closeAutoComplete, setSelected } = this.props
    const { isOpen, isActive, selected, keyword, totalCount } = autoComplete

    if (!isActive) return

    if (!isOpen && e.keyCode === KeyCode.DOWN_ARROW) {
      if (keyword && totalCount < 1) return
      openAutoComplete()
      return
    }

    if ((e.shiftKey && e.keyCode === KeyCode.TAB) || e.keyCode === KeyCode.UP_ARROW) {
      if (!isOpen) return

      if (isOpen && selected < 1) {
        this.handleResetSelectedKeyword()
        closeAutoComplete()
        return
      }

      e.preventDefault()
      setSelected(Number(selected) - 1)
      return
    }

    if (e.keyCode === KeyCode.TAB || e.keyCode === KeyCode.DOWN_ARROW) {
      if (!isOpen) return

      e.preventDefault()
      setSelected(Number(selected) + 1)

      if (this.isLastSelected()) {
        this.handleResetSelectedKeyword()
        closeAutoComplete()
      }
      return
    }
  }

  isLastSelected = () => {
    const { autoComplete } = this.props
    const { selected, totalCount } = autoComplete
    return Number(selected) === totalCount - 1
  }

  render() {
    const { cx } = this.props
    return (
      <div className={cx('search_area')} onKeyDown={this.handleKeyDown}>
        <h2 className={cx('@blind')}>검색영역</h2>
        <form name="search" method="get" onSubmit={e => e.preventDefault()}>
          <fieldset>
            <legend>네이버쇼핑 검색</legend>
            <div className={cx('inner')}>
              <SearchInput
                className={cx('search_input')}
                title="검색어 입력"
                selectedKeyword={this.state.selectedKeyword}
                onResetSelectedKeyword={this.handleResetSelectedKeyword}
                onSearch={this.handleSearch}
              />
              <AutoComplete onSaveKeywordInfo={this.handleSaveKeywordInfo} onSearch={this.handleSearch} />
            </div>
            <DetailSearch onSearch={this.handleSearch} />
          </fieldset>
        </form>
      </div>
    )
  }
}

export default connect<StateProps, DispatchProps, OwnProps>(
  (state: RootState) => ({
    autoComplete: state.autoComplete,
  }),
  {
    openAutoComplete,
    closeAutoComplete,
    setSelected,
  },
)(GnbSearch)
